'use client';

import { createContext, useContext } from 'react';
import { cn, composeTailwindRenderProps } from '@utils';
import { RadioGroup as RadioGroupPrimitive, Radio as RadioPrimitive } from 'react-aria-components';
import { tv } from 'tailwind-variants';

import type {
	RadioGroupProps as RadioGroupPrimitiveProps,
	RadioProps as RadioPrimitiveProps,
	ValidationResult,
} from 'react-aria-components';

import { Description, FieldError, FieldSuccess, Label } from './Field';

type RadioGroupContextType = {
	isValid?: boolean;
	size?: 'small' | 'large' | 'auto';
};

const RadioGroupContext = createContext<RadioGroupContextType>({
	isValid: undefined,
	size: 'auto',
});

interface RadioOption<T> {
	label: React.ReactNode | string;
	value: T;
}

interface RadioGroupProps<T> extends Omit<RadioGroupPrimitiveProps, 'children'> {
	size?: 'small' | 'large' | 'auto';
	label?: string;
	hint?: string;
	hintChildren?: React.ReactNode;
	successMessage?: string;
	errorMessage?: string | ((validation: ValidationResult) => string);
	isValid?: boolean;
	options?: RadioOption<T>[];
	ref?: React.Ref<HTMLDivElement>;
}

const RadioGroup = <T,>({
	size,
	label,
	hint,
	hintChildren,
	successMessage,
	errorMessage,
	isValid,
	options,
	ref,
	...props
}: RadioGroupProps<T>) => {
	return (
		<RadioGroupContext.Provider value={{ isValid, size }}>
			<RadioGroupPrimitive
				ref={ref}
				{...props}
				className={composeTailwindRenderProps(props.className, 'group flex flex-col gap-2')}
			>
				{label && <Label isRequired={props.isRequired}>{label}</Label>}
				{hint && <Description>{hint}</Description>}
				{hintChildren && hintChildren}
				{errorMessage ? (
					<FieldError>{errorMessage}</FieldError>
				) : (
					isValid && successMessage && <FieldSuccess>{successMessage}</FieldSuccess>
				)}
				<div
					className={cn(
						'flex gap-2 select-none group-data-[orientation=vertical]:flex-col',
						'group-data-[orientation=horizontal]:flex-wrap group-data-[orientation=horizontal]:gap-2 sm:group-data-[orientation=horizontal]:gap-4',
					)}
				>
					{options?.map(option => (
						<RadioGroup.Item
							key={String(option.value)}
							value={option.value as unknown as string}
							label={option.label}
						/>
					))}
				</div>
			</RadioGroupPrimitive>
		</RadioGroupContext.Provider>
	);
};

const radioOuterStyles = tv({
	base: ['border-light-text flex aspect-square shrink-0 rounded-full border-2'],
	variants: {
		size: {
			small: 'size-5',
			large: 'size-7.5',
			auto: 'size-5 md:size-7.5',
		},
		isHovered: {
			true: 'bg-neutral-lightest cursor-pointer',
		},
		isFocusVisible: {
			true: 'outline-light-focus ring-0 outline-3 outline-offset-4',
		},
		isSelected: {
			true: 'bg-neutral-lightest',
		},
		isInvalid: {
			true: 'ring-status-error ring-2 ring-offset-0',
		},
		isValid: {
			true: 'ring-status-success ring-2 ring-offset-0',
		},
		isDisabled: {
			true: 'cursor-not-allowed opacity-50',
		},
	},
	compoundVariants: [
		{
			isInvalid: true,
			isFocusVisible: true,
			className: 'ring-transparent',
		},
		{
			isValid: true,
			isFocusVisible: true,
			className: 'ring-transparent',
		},
	],
	defaultVariants: {
		size: 'auto',
	},
});

const radioInnerStyles = tv({
	base: 'm-auto aspect-square rounded-full',
	variants: {
		size: {
			small: 'size-3',
			large: 'size-5.5',
			auto: 'size-3 md:size-5.5',
		},
		isSelected: {
			false: '',
			true: 'bg-light-text',
		},
	},
	defaultVariants: {
		size: 'auto',
	},
});

const radioLabelStyles = tv({
	base: 'flex flex-col justify-center gap-2 font-normal',
	variants: {
		isDisabled: {
			true: 'cursor-not-allowed',
			false: 'hover:cursor-pointer',
		},
	},
});

interface RadioProps extends RadioPrimitiveProps {
	label?: React.ReactNode | string;
	ref?: React.Ref<HTMLLabelElement>;
}

const Radio = ({ label, ref, className, ...props }: RadioProps) => {
	const { isValid, size } = useContext(RadioGroupContext);

	return (
		<RadioPrimitive ref={ref} className={cn('group w-fit', className)} {...props}>
			{renderProps => (
				<div className="flex items-center gap-2">
					<div
						className={radioOuterStyles({
							size,
							...renderProps,
							isFocusVisible: renderProps.isFocused,
							isValid: isValid && renderProps.isSelected,
						})}
					>
						<div
							className={radioInnerStyles({
								size,
								...renderProps,
							})}
						/>
					</div>
					<div className={radioLabelStyles({ isDisabled: renderProps.isDisabled })}>
						{label ? <p>{label}</p> : (props.children as React.ReactNode)}
					</div>
				</div>
			)}
		</RadioPrimitive>
	);
};

RadioGroup.Item = Radio;

export type { RadioGroupProps, RadioProps };
export { RadioGroup };
